import React, { useState } from 'react';
import RichTextEditor from './RichTextEditor';
import FormattedPreview from './FormattedPreview';

interface TemplateFormProps {
  template?: any;
  onSubmit: (templateId: string | undefined, data: any) => void;
  onCancel: () => void;
  platforms: any[];
  categories: string[];
}

export default function TemplateForm({ template, onSubmit, onCancel, platforms, categories }: TemplateFormProps) {
  const [formData, setFormData] = useState({
    templateName: template?.templateName || '',
    platform: template?.platform || 'whatsapp',
    content: template?.content || '',
    contactInfo: template?.contactInfo || '',
    hashtags: template?.hashtags || [],
    categories: template?.categories || [],
    mediaTemplate: {
      includeImages: template?.mediaTemplate?.includeImages || false,
      imageCount: template?.mediaTemplate?.imageCount || 1,
      includeVideo: template?.mediaTemplate?.includeVideo || false,
      includeDocuments: template?.mediaTemplate?.includeDocuments || false,
    },
    settings: {
      autoIncludeHashtags: template?.settings?.autoIncludeHashtags || false,
      autoIncludeContactInfo: template?.settings?.autoIncludeContactInfo || false,
      maxLength: template?.settings?.maxLength || 1000,
    },
    isActive: template?.isActive !== false,
    isDefault: template?.isDefault || false,
  });

  const [newHashtag, setNewHashtag] = useState('');
  const [newCategory, setNewCategory] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (template && template.id) {
      onSubmit(template.id, formData);
    } else {
      onSubmit(undefined, formData);
    }
  };

  const addHashtag = () => {
    if (newHashtag.trim() && !formData.hashtags.includes(newHashtag.trim())) {
      setFormData({
        ...formData,
        hashtags: [...formData.hashtags, newHashtag.trim()],
      });
      setNewHashtag('');
    }
  };

  const removeHashtag = (hashtag: string) => {
    setFormData({
      ...formData,
      hashtags: formData.hashtags.filter(h => h !== hashtag),
    });
  };

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      setFormData({
        ...formData,
        categories: [...formData.categories, newCategory.trim()],
      });
      setNewCategory('');
    }
  };

  const removeCategory = (category: string) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter(c => c !== category),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Template Name *
          </label>
          <input
            type="text"
            value={formData.templateName}
            onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter template name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Platform *
          </label>
          <select
            value={formData.platform}
            onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required
          >
            {platforms.map((platform) => (
              <option key={platform.id} value={platform.id}>
                {platform.icon} {platform.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Contact Information
        </label>
        <textarea
          value={formData.contactInfo}
          onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter contact information (phone, email, etc.) that will be used for {contactInfo} variable"
          rows={3}
        />
        <p className="mt-1 text-sm text-slate-500">
          This contact information will be used when the {'{contactInfo}'} variable is used in your template content.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Content *
          </label>
          <RichTextEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
            platform={formData.platform}
            maxLength={formData.settings.maxLength}
            placeholder="Enter your message template. Use {variableName} for dynamic content."
          />
          <p className="mt-1 text-sm text-slate-500">
            Available variables: {'{birdName}'}, {'{species}'}, {'{price}'}, {'{age}'}, {'{gender}'}, {'{location}'}, {'{contactInfo}'}
          </p>
        </div>
        
        <div>
          <FormattedPreview 
            content={formData.content} 
            platform={formData.platform} 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Hashtags
          </label>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={newHashtag}
              onChange={(e) => setNewHashtag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add hashtag"
            />
            <button
              type="button"
              onClick={addHashtag}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {formData.hashtags.map((hashtag) => (
              <span key={hashtag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                #{hashtag}
                <button
                  type="button"
                  onClick={() => removeHashtag(hashtag)}
                  className="ml-1 text-cyan-600 hover:text-cyan-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Categories
          </label>
          <div className="flex space-x-2 mb-2">
            <select
              value=""
              onChange={(e) => {
                if (e.target.value && !formData.categories.includes(e.target.value)) {
                  setFormData({
                    ...formData,
                    categories: [...formData.categories, e.target.value]
                  });
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add custom category"
            />
            <button
              type="button"
              onClick={addCategory}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {formData.categories.map((category) => (
              <span key={category} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                {category}
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-slate-900 mb-3">Media Settings</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.mediaTemplate.includeImages}
                onChange={(e) => setFormData({
                  ...formData,
                  mediaTemplate: { ...formData.mediaTemplate, includeImages: e.target.checked }
                })}
                className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-slate-700">Include images</span>
            </label>
            {formData.mediaTemplate.includeImages && (
              <div className="ml-6">
                <label className="block text-sm text-slate-700 mb-1">Max images:</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.mediaTemplate.imageCount}
                  onChange={(e) => setFormData({
                    ...formData,
                    mediaTemplate: { ...formData.mediaTemplate, imageCount: parseInt(e.target.value) }
                  })}
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.mediaTemplate.includeVideo}
                onChange={(e) => setFormData({
                  ...formData,
                  mediaTemplate: { ...formData.mediaTemplate, includeVideo: e.target.checked }
                })}
                className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-slate-700">Include video</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.mediaTemplate.includeDocuments}
                onChange={(e) => setFormData({
                  ...formData,
                  mediaTemplate: { ...formData.mediaTemplate, includeDocuments: e.target.checked }
                })}
                className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-slate-700">Include documents</span>
            </label>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-slate-900 mb-3">Settings</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.settings.autoIncludeHashtags}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, autoIncludeHashtags: e.target.checked }
                })}
                className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-slate-700">Auto-include hashtags</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.settings.autoIncludeContactInfo}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, autoIncludeContactInfo: e.target.checked }
                })}
                className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-slate-700">Auto-include contact info</span>
            </label>
            <div>
              <label className="block text-sm text-slate-700 mb-1">Max length:</label>
              <input
                type="number"
                min="100"
                max="5000"
                value={formData.settings.maxLength}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, maxLength: parseInt(e.target.value) }
                })}
                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-slate-700">Active</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isDefault}
            onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-slate-700">Default template</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700"
        >
          {template ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </form>
  );
}
